/**
 * Client HTTP minimale per Microsoft Graph.
 *
 * Indipendente dal runtime (browser, Cloudflare Workers, Node 20+): usa solo fetch.
 * Si occupa di: token, retry su throttling (429/503/504) rispettando Retry-After,
 * paginazione (@odata.nextLink), $batch, errori tipizzati.
 */

export type TokenProvider = () => Promise<string>;

export interface GraphClientOptions {
  getToken: TokenProvider;
  baseUrl?: string;
  /** Numero massimo di tentativi per richiesta (default 4). */
  maxAttempts?: number;
  /** Iniettabile per test o runtime particolari. */
  fetchImpl?: typeof fetch;
}

export interface GraphRequestInit {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
  /** Per upload binari: il body viene inviato così com'è. */
  rawBody?: BodyInit;
  signal?: AbortSignal;
}

export class GraphError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = "GraphError";
  }
  get isNotFound() {
    return this.status === 404;
  }
  get isConflict() {
    return this.status === 409 || this.status === 412;
  }
  get isForbidden() {
    return this.status === 401 || this.status === 403;
  }
}

interface Page<T> {
  value: T[];
  "@odata.nextLink"?: string;
}

const RETRYABLE = new Set([429, 503, 504]);

export class GraphClient {
  private readonly baseUrl: string;
  private readonly maxAttempts: number;
  private readonly fetchImpl: typeof fetch;

  constructor(private readonly opts: GraphClientOptions) {
    this.baseUrl = (opts.baseUrl ?? "https://graph.microsoft.com/v1.0").replace(/\/$/, "");
    this.maxAttempts = opts.maxAttempts ?? 4;
    this.fetchImpl = opts.fetchImpl ?? ((...args) => fetch(...args));
  }

  /** Esegue una richiesta e restituisce il JSON (o undefined per 204). */
  async request<T = unknown>(pathOrUrl: string, init: GraphRequestInit = {}): Promise<T> {
    const res = await this.send(pathOrUrl, init);
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  /** Come request, ma restituisce la Response grezza (download, header). */
  async send(pathOrUrl: string, init: GraphRequestInit = {}): Promise<Response> {
    const url = pathOrUrl.startsWith("https://") ? pathOrUrl : `${this.baseUrl}${pathOrUrl}`;

    for (let attempt = 1; ; attempt++) {
      const token = await this.opts.getToken();
      const headers: Record<string, string> = { Authorization: `Bearer ${token}`, ...init.headers };
      let body: BodyInit | undefined = init.rawBody;
      if (init.body !== undefined) {
        headers["Content-Type"] ??= "application/json";
        body = JSON.stringify(init.body);
      }

      const res = await this.fetchImpl(url, { method: init.method ?? "GET", headers, body, signal: init.signal });
      if (res.ok) return res;

      if (RETRYABLE.has(res.status) && attempt < this.maxAttempts) {
        await sleep(retryDelayMs(res, attempt));
        continue;
      }
      throw await toGraphError(res);
    }
  }

  /** Segue @odata.nextLink fino a esaurimento (o fino a `max` elementi). */
  async getAll<T>(path: string, opts: { max?: number; headers?: Record<string, string> } = {}): Promise<T[]> {
    const out: T[] = [];
    let next: string | undefined = path;
    while (next) {
      const page: Page<T> = await this.request<Page<T>>(next, { headers: opts.headers });
      out.push(...page.value);
      if (opts.max && out.length >= opts.max) return out.slice(0, opts.max);
      next = page["@odata.nextLink"];
    }
    return out;
  }

  /** Una singola pagina, con il link alla successiva (per UI con "carica altri"). */
  async getPage<T>(pathOrNextLink: string, headers?: Record<string, string>): Promise<{ items: T[]; nextLink?: string }> {
    const page = await this.request<Page<T>>(pathOrNextLink, { headers });
    return { items: page.value, nextLink: page["@odata.nextLink"] };
  }

  /**
   * JSON batching: fino a 20 richieste per chiamata (spezza automaticamente).
   * Restituisce le risposte nell'ordine delle richieste.
   */
  async batch(requests: BatchRequest[]): Promise<BatchResponse[]> {
    const results: BatchResponse[] = [];
    for (let i = 0; i < requests.length; i += 20) {
      const chunk = requests.slice(i, i + 20).map((r, j) => ({
        id: String(i + j),
        method: r.method ?? "GET",
        url: r.url,
        ...(r.body !== undefined ? { body: r.body, headers: { "Content-Type": "application/json", ...r.headers } } : r.headers ? { headers: r.headers } : {}),
      }));
      const res = await this.request<{ responses: BatchResponse[] }>("/$batch", { method: "POST", body: { requests: chunk } });
      results.push(...res.responses.sort((a, b) => Number(a.id) - Number(b.id)));
    }
    return results;
  }
}

export interface BatchRequest {
  /** URL relativo a /v1.0, es. "/sites/{id}/lists/{id}/items". */
  url: string;
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
}
export interface BatchResponse {
  id: string;
  status: number;
  body?: unknown;
  headers?: Record<string, string>;
}

function retryDelayMs(res: Response, attempt: number): number {
  const ra = res.headers.get("Retry-After");
  const seconds = ra ? Number(ra) : NaN;
  if (Number.isFinite(seconds)) return Math.min(seconds, 60) * 1000;
  return Math.min(2 ** attempt * 500, 15_000);
}

async function toGraphError(res: Response): Promise<GraphError> {
  let code = `HTTP${res.status}`;
  let message = res.statusText;
  try {
    const data = (await res.json()) as { error?: { code?: string; message?: string } };
    code = data.error?.code ?? code;
    message = data.error?.message ?? message;
  } catch {
    /* body non JSON */
  }
  return new GraphError(res.status, code, message, res.headers.get("request-id") ?? undefined);
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Escape di una stringa per un letterale OData. */
export const odataString = (v: string) => `'${v.replace(/'/g, "''")}'`;
